import React, { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FaSave, FaPlus, FaTrash, FaStar, FaEdit, FaTimes } from "react-icons/fa";

const AdminReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingReview, setEditingReview] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        avatar: "",
        rating: 5,
        comment: "",
        date: "Hôm nay",
        verified: true
    });

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const response = await api.get("/reviews");
            setReviews(response.data);
        } catch (error) {
            toast.error("Không thể tải danh sách review");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (review = null) => {
        if (review) {
            setEditingReview(review);
            setFormData({
                name: review.name,
                avatar: review.avatar,
                rating: review.rating,
                comment: review.comment,
                date: review.date,
                verified: review.verified
            });
        } else {
            setEditingReview(null);
            setFormData({
                name: "",
                avatar: "",
                rating: 5,
                comment: "",
                date: "Hôm nay",
                verified: true
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingReview) {
                await api.put(`/admin/reviews/${editingReview.id}`, formData);
                toast.success("Cập nhật review thành công");
            } else {
                await api.post("/admin/reviews", formData);
                toast.success("Thêm review thành công");
            }
            setShowModal(false);
            fetchReviews();
        } catch (error) {
            toast.error("Lỗi khi lưu review");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc muốn xóa review này?")) {
            try {
                await api.delete(`/admin/reviews/${id}`);
                toast.success("Đã xóa review");
                fetchReviews();
            } catch (error) {
                toast.error("Lỗi khi xóa review");
            }
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-medium">Đang tải danh sách...</div>;

    return (
        <div className="p-4 sm:p-8 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 uppercase tracking-tight">Quản lý Reviews</h1>
                    <p className="text-sm text-gray-500 font-medium mt-1">Quản lý đánh giá từ khách hàng hiển thị trên trang chủ</p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-all font-black shadow-xl shadow-red-500/20 uppercase tracking-widest text-xs sm:text-sm"
                >
                    <FaPlus size={14} />
                    <span>Thêm Review</span>
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reviews.map((review) => (
                    <div key={review.id} className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-5 sm:p-6 flex flex-col relative group hover:shadow-xl transition-all duration-300">
                        <div className="flex items-center gap-4 mb-4">
                            <img
                                src={review.avatar || "https://via.placeholder.com/150"}
                                alt={review.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-50"
                            />
                            <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-gray-900 truncate">{review.name}</h3>
                                <div className="flex text-yellow-400 text-xs">
                                    {[...Array(5)].map((_, i) => (
                                        <FaStar key={i} className={i < review.rating ? "fill-current" : "text-gray-200"} />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm italic line-clamp-4 flex-grow mb-6">"{review.comment}"</p>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-gray-50 mt-auto">
                            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">{review.date}</span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleOpenModal(review)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                    <FaEdit size={16} />
                                </button>
                                <button
                                    onClick={() => handleDelete(review.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <FaTrash size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {reviews.length === 0 && (
                <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-gray-100">
                    <p className="text-gray-400 font-medium">Chưa có đánh giá nào. Hãy thêm đánh giá đầu tiên!</p>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-lg shadow-2xl relative animate-slideUp overflow-hidden max-h-[95vh] flex flex-col">
                        <div className="p-6 sm:p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                            <h2 className="text-lg sm:text-xl font-black text-gray-900 uppercase tracking-tight">
                                {editingReview ? "Sửa Review" : "Thêm Review mới"}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-2">
                                <FaTimes size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-5 overflow-y-auto">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Tên khách hàng</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-bold"
                                        placeholder="Nguyễn Văn A"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Đánh giá (1-5 sao)</label>
                                    <select
                                        value={formData.rating}
                                        onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                                        className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-bold"
                                    >
                                        {[5, 4, 3, 2, 1].map(num => <option key={num} value={num}>{num} Sao</option>)}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Link ảnh đại diện (URL)</label>
                                <input
                                    type="text"
                                    value={formData.avatar}
                                    onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                                    className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
                                    placeholder="https://example.com/avatar.jpg"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Thời gian (VD: 2 ngày trước)</label>
                                <input
                                    type="text"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                    className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Nội dung đánh giá</label>
                                <textarea
                                    required
                                    rows="4"
                                    value={formData.comment}
                                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                                    className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all italic"
                                    placeholder="Chia sẻ trải nghiệm của khách hàng..."
                                ></textarea>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="verified"
                                    checked={formData.verified}
                                    onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="verified" className="text-sm font-bold text-gray-600">Verified Purchase (Đã mua hàng)</label>
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-red-600 text-white rounded-2xl py-4 font-black uppercase tracking-widest hover:bg-red-700 transition-all shadow-xl shadow-red-500/20 flex items-center justify-center gap-3 mt-4"
                            >
                                <FaSave size={20} />
                                <span>{editingReview ? "Cập nhật Review" : "Đăng Review ngay"}</span>
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminReviews;
